/**
 * BackgroundLockHost
 *
 * Mounts once at the app root. Watches page visibility + activity. When the
 * user returns after > REAUTH.BACKGROUND_LOCK_MS (30 min) and is
 * authenticated, we render a full-screen lock overlay that requires a
 * biometric unlock (or password fallback via StepUp). No lock is applied on
 * public routes.
 *
 * Also refreshes `markActiveNow()` on user interaction so short absences
 * don't trigger the lock.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ScanFace, Fingerprint, Loader2, KeyRound, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import {
  loginWithPasskey,
  prepareLoginWithPasskey,
  hasPasskey as apiHasPasskey,
  platformAuthenticatorAvailable,
  needsBackgroundUnlock,
  markUnlockedNow,
  markActiveNow,
  enrollPasskey,
  listPasskeys,
  deletePasskey,
} from '../../utils/webauthn';
import { useStepUp } from './StepUpProvider';
import { useIsStandalone } from '../../utils/pwa';
import {
  useIsNative,
  getBiometryInfo,
  nativeBiometricAuth,
  nativeHaptic,
} from '../../utils/nativeBridge';

const PUBLIC_PREFIX = ['/', '/auth', '/login', '/signup', '/pricing', '/help', '/privacy-policy', '/terms-of-service', '/cookie-policy', '/camera-test', '/verify-email', '/verify-email-pending', '/reset-password', '/accept-invitation', '/portal/employee', '/oauth/plaid', '/app-welcome', '/onboarding'];
const isPublic = (path) => PUBLIC_PREFIX.some((p) => path === p || path.startsWith(p + '/'));

const LOGO_FULL = '/akountfly-logo-full.png';

/**
 * Best-guess label for the OS biometric on this device.
 * - Native wrapper: use the exact `getBiometryInfo().type` (definitive).
 * - Browser PWA: fall back to UA sniffing so we never say "Face ID" on
 *   Android/Windows or "Windows Hello" on iPhone.
 */
const guessBiometricLabel = (nativeInfo) => {
  if (nativeInfo?.type === 'face') return { label: 'Face ID', Icon: ScanFace };
  if (nativeInfo?.type === 'fingerprint') {
    return {
      label: nativeInfo.platform === 'ios' ? 'Touch ID' : 'Fingerprint',
      Icon: Fingerprint,
    };
  }
  if (nativeInfo?.type === 'iris') return { label: 'Iris unlock', Icon: ScanFace };
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/iPhone|iPod/i.test(ua)) return { label: 'Face ID / Touch ID', Icon: ScanFace };
  if (/iPad/i.test(ua)) return { label: 'Touch ID / Face ID', Icon: Fingerprint };
  if (/Mac/i.test(ua)) return { label: 'Touch ID', Icon: Fingerprint };
  if (/Android/i.test(ua)) return { label: 'Fingerprint', Icon: Fingerprint };
  if (/Windows/i.test(ua)) return { label: 'Windows Hello', Icon: ScanFace };
  return { label: 'biometric unlock', Icon: ScanFace };
};

export default function BackgroundLockHost() {
  const { user } = useAuth() || {};
  const { pathname } = useLocation();
  const { requireStepUp } = useStepUp();
  const isPwa = useIsStandalone();
  const isNative = useIsNative();
  const [locked, setLocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [biometricInfo, setBiometricInfo] = useState(null); // { type, platform } when native
  const [showRecovery, setShowRecovery] = useState(false); // "Set up Face ID again" CTA after a failure
  const preparedLoginRef = useRef(null); // Pre-fetched WebAuthn challenge (see below)
  const evaluatingRef = useRef(false);

  // Keep last-active timestamp fresh so short visibility flickers don't
  // trigger the lock unnecessarily.
  //
  // Native-app rule (iter1086h): when running inside our native wrapper
  // (`window.NativeApp.isNative === true`), the OS/native app owns
  // foreground / background / idle lock entirely. Skip PWA bookkeeping
  // so we don't fight the native lifecycle.
  useEffect(() => {
    if (isNative) return;
    if (!isPwa || !user || isPublic(pathname)) return;
    const bump = () => markActiveNow();
    bump();
    const events = ['pointerdown', 'keydown', 'scroll', 'wheel'];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, bump));
  }, [isPwa, isNative, user, pathname]);

  // Evaluate lock on visibility restore / pageshow / user change.
  useEffect(() => {
    // Native app: OS owns lock. Never show the PWA unlock screen and
    // never run the 30-min idle timer here - StepUpProvider still
    // covers sensitive actions via window.NativeApp.biometricAuth().
    if (isNative) { setLocked(false); return; }
    // Auto-lock is PWA-only. On regular web sessions the browser handles
    // idle behaviour and users would find a full-screen Face-ID overlay
    // deeply unexpected there.
    if (!isPwa) { setLocked(false); return; }
    if (!user) { setLocked(false); return; }
    if (isPublic(pathname)) { setLocked(false); return; }

    const evaluate = async () => {
      if (evaluatingRef.current) return;
      evaluatingRef.current = true;
      try {
        if (!needsBackgroundUnlock()) return;
        // Native wrapper: probe OS biometry through the bridge. Android
        // WebView / WKWebView have no WebAuthn, so we skip
        // `platformAuthenticatorAvailable` entirely here.
        if (isNative) {
          const info = await getBiometryInfo();
          const has = !!(info.available && info.enrolled);
          setBiometricInfo(info);
          setPasskeyAvailable(has);
          if (has) setLocked(true);
          else markUnlockedNow();
          return;
        }
        const available = await platformAuthenticatorAvailable();
        const has = available && user.email ? await apiHasPasskey(user.email) : false;
        // Only enforce lock when biometric is actually usable OR user opted
        // in to strict lock. If no biometric available, we don't lock the
        // session mid-flight - the 30-day refresh cookie still governs
        // hard expiry.
        setBiometricInfo(null); // browser PWA - fall back to UA sniff
        setPasskeyAvailable(has);
        if (has) setLocked(true);
        else markUnlockedNow(); // reset so we don't retry every visibility change
      } finally {
        evaluatingRef.current = false;
      }
    };

    // Fresh mount / route change
    evaluate();

    const onVisibility = () => { if (document.visibilityState === 'visible') evaluate(); };
    const onPageShow = () => evaluate();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onPageShow);
    };
  }, [isPwa, isNative, user, pathname]);

  // Pre-fetch the WebAuthn challenge as soon as the lock screen is shown
  // and biometric is available. iOS Safari enforces a ~5-second user
  // activation window: if the network round trip for /login-options runs
  // AFTER the user tap, `navigator.credentials.get()` can miss the window
  // and iOS silently refuses to show the Face ID prompt. By fetching the
  // publicKey object at mount time, the click handler can call
  // `navigator.credentials.get()` synchronously - the Face ID sheet
  // appears the instant the user taps.
  useEffect(() => {
    if (!locked || !passkeyAvailable || isNative || !user?.email) return;
    if (preparedLoginRef.current) return; // already prepared
    let cancelled = false;
    (async () => {
      try {
        const prep = await prepareLoginWithPasskey(user.email);
        if (!cancelled) preparedLoginRef.current = prep;
      } catch (_) { /* noop - falls back to on-click fetch */ }
    })();
    return () => { cancelled = true; };
  }, [locked, passkeyAvailable, isNative, user?.email]);

  const doUnlock = async () => {
    setUnlocking(true);
    try {
      // Native path: real OS Face ID / Touch ID / Fingerprint via the bridge.
      if (isNative && passkeyAvailable) {
        const res = await nativeBiometricAuth('Unlock AkountFly');
        if (res.success) {
          nativeHaptic('success');
          markUnlockedNow();
          toast.success('Unlocked');
          setLocked(false);
          return;
        }
        // Any biometric failure (user cancel, lockout, not-enrolled) falls
        // through to password step-up so the user is never dead-ended.
        if (res.error && res.error !== 'user_cancel' && res.error !== 'not_native') {
          toast.error(`${bioLabel} unavailable. Please sign in with your password.`);
        }
        const ok = await requireStepUp({ reason: 'Verify your identity to unlock AkountFly', force: true, passwordOnly: true });
        if (ok) { markUnlockedNow(); setLocked(false); }
        return;
      }

      if (passkeyAvailable && user?.email) {
        // Use the pre-fetched challenge if available so the WebAuthn call
        // runs inside the same user-activation frame as the tap. Falls
        // back to a fresh network fetch if pre-fetch didn't complete.
        const prep = preparedLoginRef.current;
        preparedLoginRef.current = null; // one-shot
        if (prep) {
          // Give the OS as much of the click's activation as possible -
          // no `await` between the tap and navigator.credentials.get().
          const cred = await navigator.credentials.get({ publicKey: prep.publicKey });
          await prep.verify(cred);
        } else {
          await loginWithPasskey(user.email);
        }
        markUnlockedNow();
        toast.success('Unlocked');
        setLocked(false);
      } else {
        const ok = await requireStepUp({ reason: 'Verify your identity to unlock AkountFly', force: true, passwordOnly: true });
        if (ok) { markUnlockedNow(); setLocked(false); }
      }
    } catch (e) {
      const name = String(e?.name || '');
      const msg = String(e?.message || '');
      const lower = msg.toLowerCase();
      // Browsers deliberately return a single generic error for a wide
      // spectrum of WebAuthn failures (user cancel, timeout, no matching
      // credential on this RP-ID, credential wiped from Keychain). We
      // treat any NotAllowedError as "biometric is not going to work on
      // this attempt" and fall to password without a scary toast.
      const isNotAllowed =
        name === 'NotAllowedError' ||
        lower.includes('not allowed') ||   // "The operation ... was not allowed by the user agent"
        lower.includes('notallowed') ||    // legacy string (rare)
        lower.includes('cancel') ||
        lower.includes('abort');
      // Explicit RP-ID / credential-not-recognized signals that some
      // browsers do surface. When we see these, purge the local passkey
      // capability so we don't loop into another biometric prompt.
      const isRpMismatch =
        lower.includes('no matching') ||
        lower.includes('rp id') ||
        lower.includes('rpid') ||
        name === 'SecurityError' ||
        lower.includes('security error');
      // Server-side rejection AFTER Face ID succeeded on device (e.g.
      // stale challenge, credential-DB drift, signature counter regression).
      // Face ID hardware isn't broken - the server rejected the assertion.
      // Silently fall to password; don't blame the user's biometric.
      const isServerReject =
        lower.includes('passkey_verify_failed') ||
        lower.includes('passkey_not_recognized') ||
        lower.includes('passkey_wrong_account') ||
        lower.includes('challenge expired') ||
        lower.includes('could not start');
      // Diagnostic: single console line captures name+msg for support
      // debugging without spamming users. Safe to leave in prod.
      try { console.warn('[akf.biometric] unlock failed', { name, msg }); } catch (_) { /* noop */ }
      if (isRpMismatch || isNotAllowed || isServerReject) {
        setPasskeyAvailable(false); // stop offering biometric this session
        setShowRecovery(true); // let the user re-enroll a fresh passkey right here
        if (isRpMismatch) {
          toast.error(`This ${bioLabel} was set up in a different place. Sign in with your password and we'll offer to set it up again.`);
        }
        // For NotAllowedError / server-reject we DO NOT toast - either
        // user cancelled, the platform silently refused, or the server
        // rejected the assertion. In all cases the password modal below
        // is the recovery UX; a scary "Face ID unavailable" toast blames
        // the wrong thing.
      } else {
        setShowRecovery(true);
        toast.error(`${bioLabel} unavailable. Please sign in with your password.`);
      }
      // Route directly to password - no second biometric attempt.
      const ok = await requireStepUp({ reason: 'Verify your identity to unlock AkountFly', force: true, passwordOnly: true });
      if (ok) { markUnlockedNow(); setLocked(false); }
    } finally {
      setUnlocking(false);
    }
  };

  // Direct password path — bypasses biometric entirely so users on a fresh
  // device (or after a passkey purge) always have a visible way out.
  const doUnlockWithPassword = async () => {
    setUnlocking(true);
    try {
      const ok = await requireStepUp({ reason: 'Sign in to continue', force: true, passwordOnly: true });
      if (ok) { markUnlockedNow(); setLocked(false); }
    } finally {
      setUnlocking(false);
    }
  };

  // Self-heal path: user's stored passkey has gone stale (device wipe,
  // iCloud sync loss, corrupt cred row) - after they verify their
  // password, purge any stale passkeys for this device and enroll a
  // fresh one on the spot. This gives users a permanent escape hatch
  // instead of being stuck in "biometric fails every time".
  const doResetBiometric = async () => {
    if (isNative) {
      // Native path: OS handles enrollment; we just clear our local flag
      // and open the password step-up so user unlocks and can re-enable
      // from Settings.
      try { localStorage.removeItem('akf_native_biometric_enrolled'); } catch (_) { /* noop */ }
      const ok = await requireStepUp({ reason: 'Sign in to reset biometric', force: true, passwordOnly: true });
      if (ok) {
        markUnlockedNow();
        setLocked(false);
        toast.info(`You can re-enable ${bioLabel} in Settings > Security.`);
      }
      return;
    }
    setUnlocking(true);
    try {
      // Step 1: verify identity with password (proof of presence).
      const ok = await requireStepUp({ reason: `Verify to reset ${bioLabel}`, force: true, passwordOnly: true });
      if (!ok) return;
      // Step 2: session is fresh - remove stale passkeys for this device.
      try {
        const existing = await listPasskeys();
        for (const p of existing) {
          try { await deletePasskey(p.id); } catch (_) { /* noop */ }
        }
      } catch (_) { /* noop */ }
      // Step 3: enroll a fresh passkey inline while the session is warm.
      try {
        await enrollPasskey();
        toast.success(`${bioLabel} set up. Unlock instantly next time.`);
      } catch (e) {
        const name = String(e?.name || '');
        const msg = String(e?.message || '').toLowerCase();
        const isCancel = name === 'NotAllowedError' || msg.includes('not allowed') || msg.includes('cancel') || msg.includes('abort');
        if (!isCancel) {
          toast.info(`Couldn't set up ${bioLabel} right now. You can enable it later in Settings > Security.`);
        }
      }
      markUnlockedNow();
      setLocked(false);
    } finally {
      setUnlocking(false);
    }
  };

  // Pick label + icon based on the OS (native = definitive, browser = UA sniff)
  const { label: bioLabel, Icon: BioIcon } = guessBiometricLabel(biometricInfo);

  // Native app owns the lock lifecycle. Render nothing here so we never
  // stack a PWA "Unlock with Face ID" screen over the native one.
  if (isNative) return null;

  return (
    <AnimatePresence>
      {locked && (
        <motion.div
          className="fixed inset-0 z-[90] flex flex-col items-center justify-center text-white overflow-hidden print:hidden"
          style={{ background: 'radial-gradient(120% 90% at 50% 0%, #33499B 0%, #283F8B 45%, #1B2C66 100%)' }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="background-lock-screen"
        >
          <img src={LOGO_FULL} alt="AkountFly" className="h-9 object-contain mb-10" style={{ filter: 'brightness(0) invert(1)' }} />
          <motion.div
            className="w-24 h-24 rounded-3xl bg-white/12 border border-white/20 backdrop-blur-lg flex items-center justify-center relative shadow-2xl"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <motion.span
              className="absolute inset-0 rounded-3xl bg-[#5FC6C5]/40"
              animate={{ scale: [1, 1.35], opacity: [0.4, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
            />
            <BioIcon className="w-12 h-12 text-white relative" strokeWidth={1.4} />
          </motion.div>
          <h1 className="mt-8 text-2xl font-extrabold tracking-tight" data-testid="background-lock-greeting">
            Welcome back{user?.full_name ? `, ${String(user.full_name).split(' ')[0]}` : ''}
          </h1>
          <p className="mt-1.5 text-white/70 text-sm px-8 text-center max-w-xs">
            Unlock with {passkeyAvailable ? bioLabel : 'your password'} to continue.
          </p>
          {passkeyAvailable && (
            <button
              onClick={doUnlock}
              disabled={unlocking}
              className="mt-8 h-14 px-8 rounded-full bg-white text-[#283F8B] text-[16px] font-extrabold shadow-[0_10px_30px_rgba(0,0,0,0.35)] active:scale-95 transition-transform disabled:opacity-70 inline-flex items-center gap-2"
              data-testid="background-lock-unlock-btn"
            >
              {unlocking ? (<><Loader2 className="w-4 h-4 animate-spin" /> Unlocking...</>) : (<><BioIcon className="w-5 h-5" /> Unlock with {bioLabel}</>)}
            </button>
          )}
          <button
            onClick={doUnlockWithPassword}
            disabled={unlocking}
            className={`${passkeyAvailable ? 'mt-4' : 'mt-8'} h-12 px-6 rounded-full ${passkeyAvailable ? 'bg-transparent text-white/85 hover:bg-white/10 border border-white/25' : 'bg-white text-[#283F8B] font-extrabold shadow-[0_10px_30px_rgba(0,0,0,0.35)]'} text-[14px] font-semibold active:scale-95 transition-all disabled:opacity-70 inline-flex items-center gap-2`}
            data-testid="background-lock-password-btn"
          >
            <KeyRound className="w-4 h-4" /> Sign in with password
          </button>
          {showRecovery && (
            <button
              onClick={doResetBiometric}
              disabled={unlocking}
              className="mt-3 h-10 px-4 rounded-full text-white/70 hover:text-white text-[13px] font-semibold active:scale-95 transition-all disabled:opacity-60 inline-flex items-center gap-2"
              data-testid="background-lock-reset-biometric-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Set up {bioLabel} again on this device
            </button>
          )}
          <p className="text-white/50 text-[11px] mt-6">Auto-locked after 30 min of inactivity</p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
